import { supabase } from '../supabase';

/**
 * Service for managing FattureInCloud configuration
 */

export interface FICConfig {
  id: string;
  organization_id: string;
  company_id: number | null;
  company_name: string | null;
  is_connected: boolean;
  access_token: string | null;
  refresh_token: string | null;
  token_expires_at: string | null;
  last_connected_at: string | null;
  last_sync_at: string | null;
  default_vat_rate_id: number | null;
  default_vat_rate_name: string | null;
  default_vat_rate_value: number | null;
  default_payment_method_id: number | null;
  default_payment_method_name: string | null;
  template_id: number | null;
  template_name: string | null;
  settings: any;
  // Nuovi campi per fatturazione automatica
  default_stamp_duty: boolean;
  auto_issue_enabled: boolean;
  issue_days_before_due: number;
  // Rivalsa INPS di default
  default_rivalsa_enabled: boolean;
}

export interface FICVatType {
  id: number;
  value: number;
  description: string;
  notes: string;
  e_invoice: boolean;
  ei_type: string;
  ei_description: string;
  editable: boolean;
  is_disabled: boolean;
}

export interface FICPaymentMethod {
  id: number;
  name: string;
  type: string;
  is_default: boolean;
  default_payment_account?: {
    id: number;
    name: string;
    type: string;
  };
}

export interface FICTemplate {
  id: number;
  name: string;
  type: string;
}

export const ficConfigService = {
  /**
   * Get FattureInCloud configuration for organization
   */
  async getConfig(organizationId: string): Promise<FICConfig | null> {
    try {
      const { data, error } = await supabase
        .from('fattureincloud_configs')
        .select('*')
        .eq('organization_id', organizationId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No config found
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error getting FIC config:', error);
      throw error;
    }
  },

  /**
   * Update default settings
   */
  async updateDefaults(
    organizationId: string,
    defaults: {
      vat_rate?: { id: number; name: string; value: number };
      payment_method?: { id: number; name: string };
      template?: { id: number; name: string };
    }
  ): Promise<void> {
    try {
      const updateData: any = {};

      if (defaults.vat_rate) {
        updateData.default_vat_rate_id = defaults.vat_rate.id;
        updateData.default_vat_rate_name = defaults.vat_rate.name;
        updateData.default_vat_rate_value = defaults.vat_rate.value;
      }

      if (defaults.payment_method) {
        updateData.default_payment_method_id = defaults.payment_method.id;
        updateData.default_payment_method_name = defaults.payment_method.name;
      }

      if (defaults.template) {
        updateData.template_id = defaults.template.id;
        updateData.template_name = defaults.template.name;
      }

      const { error } = await supabase
        .from('fattureincloud_configs')
        .update(updateData)
        .eq('organization_id', organizationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating FIC defaults:', error);
      throw error;
    }
  },

  /**
   * Update invoice settings (auto issue, stamp duty default, etc.)
   */
  async updateInvoiceSettings(
    organizationId: string,
    settings: {
      default_stamp_duty?: boolean;
      auto_issue_enabled?: boolean;
      issue_days_before_due?: number;
      default_rivalsa_enabled?: boolean;
    }
  ): Promise<void> {
    try {
      const updateData: any = {};

      if (settings.default_stamp_duty !== undefined) {
        updateData.default_stamp_duty = settings.default_stamp_duty;
      }

      if (settings.auto_issue_enabled !== undefined) {
        updateData.auto_issue_enabled = settings.auto_issue_enabled;
      }

      if (settings.issue_days_before_due !== undefined) {
        updateData.issue_days_before_due = settings.issue_days_before_due;
      }

      if (settings.default_rivalsa_enabled !== undefined) {
        updateData.default_rivalsa_enabled = settings.default_rivalsa_enabled;
      }

      const { error } = await supabase
        .from('fattureincloud_configs')
        .update(updateData)
        .eq('organization_id', organizationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating FIC invoice settings:', error);
      throw error;
    }
  },

  /**
   * Test connection by verifying token
   */
  async testConnection(organizationId: string): Promise<boolean> {
    try {
      // Test by fetching VAT types (lightweight API call)
      const { data, error } = await supabase.functions.invoke('fattureincloud-info', {
        body: {
          organization_id: organizationId,
          info_type: 'vat_types'
        }
      });

      if (error) {
        console.error('Test connection error:', error);
        return false;
      }

      return data?.success === true;
    } catch (error) {
      console.error('Error testing FIC connection:', error);
      return false;
    }
  },

  /**
   * Fetch VAT rates from FattureInCloud (via edge function to avoid CORS)
   */
  async fetchVatRates(organizationId: string): Promise<FICVatType[]> {
    try {
      const { data, error } = await supabase.functions.invoke('fattureincloud-info', {
        body: {
          organization_id: organizationId,
          info_type: 'vat_types'
        }
      });

      if (error) {
        console.error('Error fetching VAT rates:', error);
        throw error;
      }

      return data?.vat_types || [];
    } catch (error) {
      console.error('Error fetching VAT rates:', error);
      return [];
    }
  },

  /**
   * Fetch payment methods from FattureInCloud (via edge function to avoid CORS)
   */
  async fetchPaymentMethods(organizationId: string): Promise<FICPaymentMethod[]> {
    try {
      const { data, error } = await supabase.functions.invoke('fattureincloud-info', {
        body: {
          organization_id: organizationId,
          info_type: 'payment_methods'
        }
      });

      if (error) {
        console.error('Error fetching payment methods:', error);
        throw error;
      }

      return data?.payment_methods || [];
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      return [];
    }
  },

  /**
   * Fetch templates from FattureInCloud (via edge function to avoid CORS)
   */
  async fetchTemplates(organizationId: string): Promise<FICTemplate[]> {
    try {
      const { data, error } = await supabase.functions.invoke('fattureincloud-info', {
        body: {
          organization_id: organizationId,
          info_type: 'templates'
        }
      });

      if (error) {
        console.error('Error fetching templates:', error);
        throw error;
      }

      return data?.templates || [];
    } catch (error) {
      console.error('Error fetching templates:', error);
      return [];
    }
  },

  /**
   * Log an operation in settings
   */
  async logOperation(
    organizationId: string,
    operation: string,
    status: 'success' | 'error',
    details: any
  ): Promise<void> {
    try {
      const config = await this.getConfig(organizationId);
      if (!config) return;

      const logs = config.settings?.logs || [];

      // Add new log entry
      logs.unshift({
        timestamp: new Date().toISOString(),
        operation,
        status,
        details
      });

      // Keep only last 100 entries
      const trimmedLogs = logs.slice(0, 100);

      const { error } = await supabase
        .from('fattureincloud_configs')
        .update({
          settings: {
            ...config.settings,
            logs: trimmedLogs
          }
        })
        .eq('organization_id', organizationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error logging FIC operation:', error);
      // Don't throw - logging failures shouldn't break the flow
    }
  },

  /**
   * Get operation logs
   */
  async getLogs(organizationId: string): Promise<any[]> {
    try {
      const config = await this.getConfig(organizationId);
      return config?.settings?.logs || [];
    } catch (error) {
      console.error('Error getting FIC logs:', error);
      return [];
    }
  }
};
